import { resetState, updateTokens } from '@/stores/authStore';
import { idbStorage } from '@/utils/idbStorage';
import { handleError as globalErrorHandler } from '@/utils/errorHandler';
// import { securityManager } from '@/utils/security'; // Rate limiting removal pending phase 2 cleanup
import { logger } from '@/utils/logger';
import toast from 'react-hot-toast';
import { ApiResponse } from '@/constants/Interfaces';

const BASE_URL = import.meta.env.VITE_API_URL;
export const appBaseUrl = import.meta.env.VITE_APP_BASE_URL;

// Add flags to track authentication state
let isRefreshing = false;
let isLoggingOut = false;
let failedQueue: Array<{
  resolve: (value: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const getToken = async (): Promise<string | null> => {
  try {
    // Try to get token from secure storage first
    const persistedState = (await idbStorage.secureGetItem('auth-store')) as {
      state?: { userInfo?: { token?: string } };
    };

    if (persistedState?.state?.userInfo?.token) {
      return persistedState.state.userInfo.token;
    }

    // Fallback to regular storage for backward compatibility
    const fallbackState = (await idbStorage.getItem('auth-store')) as {
      state?: { userInfo?: { token?: string } };
    };

    if (fallbackState?.state?.userInfo?.token) {
      return fallbackState.state.userInfo.token;
    }

    return null;
  } catch (error) {
    logger.error('Failed to get token from storage', {
      error: error instanceof Error ? error.message : error
    });
    return null;
  }
};

const getRefreshToken = async (): Promise<string | null> => {
  try {
    // Try to get refresh token from secure storage first
    const persistedState = (await idbStorage.secureGetItem('auth-store')) as {
      state?: { userInfo?: { refresh_token?: string } };
    };

    if (persistedState?.state?.userInfo?.refresh_token) {
      return persistedState.state.userInfo.refresh_token;
    }

    // Fallback to regular storage for backward compatibility
    const fallbackState = (await idbStorage.getItem('auth-store')) as {
      state?: { userInfo?: { refresh_token?: string } };
    };
    return fallbackState?.state?.userInfo?.refresh_token ?? null;
  } catch (error) {
    logger.error('Failed to get refresh token from storage', {
      error: error instanceof Error ? error.message : error
    });
    return null;
  }
};

interface RefreshTokenResponse {
  success: boolean;
  data?: {
    token?: string;
    access_token?: string;
    accessToken?: string;
    refresh_token?: string;
    refreshToken?: string;
    [key: string]: unknown;
  };
  token?: string;
  access_token?: string;
  accessToken?: string;
  refresh_token?: string;
  refreshToken?: string;
  [key: string]: unknown;
}

// Refresh token function
export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refresh_token = await getRefreshToken();

    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept-language': 'en',
        'x-hakuba-platform': 'web',
        'x-hakuba-version': '1.0.0',
        refresh_token: refresh_token
      }
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = (await response.json()) as RefreshTokenResponse;

    if (data.success && data.data) {
      // Check different possible token field locations
      const token =
        data.data.token || data.data.access_token || data.data.accessToken;
      const refreshToken = data.data.refresh_token || data.data.refreshToken;

      if (token && refreshToken) {
        // Update tokens in store
        updateTokens(token, refreshToken);
        return token;
      } else {
        throw new Error('Missing token fields in refresh response');
      }
    } else if (data.success) {
      // Check if tokens are at root level
      const token = data.token || data.access_token || data.accessToken;
      const refreshToken = data.refresh_token || data.refreshToken;

      if (token && refreshToken) {
        // Update tokens in store
        updateTokens(token, refreshToken);
        return token;
      } else {
        throw new Error('Missing token fields in refresh response');
      }
    } else {
      const allKeys = Object.keys(data);

      for (const key of allKeys) {
        const value = data[key];
        if (typeof value === 'object' && value !== null) {
          const nested = value as Record<string, string>;
          const nestedToken =
            nested.token || nested.access_token || nested.accessToken;
          const nestedRefreshToken =
            nested.refresh_token || nested.refreshToken;

          if (nestedToken && nestedRefreshToken) {
            updateTokens(nestedToken, nestedRefreshToken);
            return nestedToken;
          }
        }
      }
      throw new Error('Invalid refresh token response');
    }
  } catch {
    return null;
  }
};

interface RequestOptions extends RequestInit {
  headers?: Record<string, string>;
  skipAuthRefresh?: boolean; // Flag to prevent infinite loops
  isLogoutRequest?: boolean; // Flag to identify logout requests
}

const request = async <T = unknown>(
  url: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> => {
  const token = await getToken();

  // Rate limiting check REMOVED
  /*
  const method = (options.method || 'GET').toUpperCase();
  const requestId = `${method}:${url}`;
  if (!securityManager.checkRateLimit(requestId)) {
    logger.warn('Rate limit exceeded', { url });
    return {
      success: false,
      status: 429,
      message: 'Too many requests. Please wait a moment and try again.'
    };
  }
  */

  try {
    options.headers = {
      ...options.headers,
      'accept-language': 'en',
      'x-hakuba-platform': 'web',
      'x-hakuba-version': '1.0.0',
      'X-Requested-With': 'XMLHttpRequest'
    };

    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        credentials: 'include'
      };
    }

    const response = await fetch(url, options);

    // Handle 401 Unauthorized
    if (response.status === 401 && !isLoggingOut && !options.skipAuthRefresh) {
      // Special handling for logout API - don't refresh token, logout directly
      if (options.isLogoutRequest || url.includes('/auth/logout') || !token) {
        handleLogout(await response.json());
        return {
          success: false,
          status: 401,
          message: 'Session expired during logout'
        };
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(_token => {
            // Retry the original request with new token
            return request<T>(url, { ...options, skipAuthRefresh: true });
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          processQueue(null, newToken);

          // Retry the original request with new token
          const retryResponse = await request<T>(url, {
            ...options,
            skipAuthRefresh: true
          });
          return retryResponse;
        } else {
          // Refresh failed, logout user
          processQueue(new Error('Token refresh failed'));
          handleLogout(await response.json());
          return {
            success: false,
            status: 401,
            message: 'Authentication failed'
          };
        }
      } catch (error) {
        processQueue(error);
        handleLogout(await response.json());
        return {
          success: false,
          status: 401,
          message: 'Authentication failed'
        };
      } finally {
        isRefreshing = false;
      }
    }
    if (response.status === 404) {
      toast.error('Resource not found');
    }

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const jsonResponse = await response.json();
      return {
        ...jsonResponse,
        status: response.status
      };
    }

    return {
      success: false,
      status: response.status,
      message: 'Invalid response format'
    };
  } catch (error: unknown) {
    let message = 'API request failed';
    if (error instanceof Error) {
      message = `API request failed: ${error.message}`;
    }
    return {
      success: false,
      message
    };
  }
};

// Handle logout process
const handleLogout = (response: { message?: string }): void => {
  if (isLoggingOut) return; // Prevent multiple executions
  isLoggingOut = true;
  toast.remove(); // Remove any existing toasts

  const message = response?.message ?? 'Session has been expired';
  toast.error(message, {
    duration: 3000
  });

  // Clear auth data
  resetState();
  idbStorage.removeItem('auth-store');

  // Reset flags
  setTimeout(() => {
    isLoggingOut = false;
    isRefreshing = false;
    failedQueue = [];
  }, 1000);
};

export const handleError = async (response: ApiResponse<unknown>) => {
  const { status } = response ?? {};

  if (status === 401) {
    // Skip showing error if already logging out
    if (!isLoggingOut) {
      resetState();
      await idbStorage.removeItem('auth-store');
    }
    return;
  }

  // Use global error handler for better error management
  globalErrorHandler(response, {
    showToast: true,
    logError: true,
    context: { source: 'api_service' }
  });
};

export const get = <T = unknown>(url: string, options: RequestOptions = {}) =>
  request<T>(`${BASE_URL}${url}`, options);

export const post = <T = unknown>(
  url: string,
  data?: Record<string, unknown> | FormData,
  options: RequestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  }
) => {
  let body: BodyInit | undefined;

  if (data instanceof FormData) {
    const restHeaders = { ...(options.headers || {}) };
    delete restHeaders['Content-Type'];
    options.headers = restHeaders;
    body = data;
  } else {
    body = JSON.stringify(data);
  }

  return request<T>(`${BASE_URL}${url}`, {
    ...options,
    body
  });
};

const sendRequest = <T = unknown>(
  method: 'PUT' | 'DELETE' | 'PATCH',
  url: string,
  data?: Record<string, unknown>,
  customOptions: RequestOptions = {}
) => {
  const payload = { ...data };
  return request<T>(`${BASE_URL}${url}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(customOptions.headers ?? {})
    },
    ...customOptions,
    body: JSON.stringify(payload)
  });
};

export const put = <T = unknown>(
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions
) => sendRequest<T>('PUT', url, data, options);

export const del = <T = unknown>(
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions
) => sendRequest<T>('DELETE', url, data, options);

export const patch = <T = unknown>(
  url: string,
  data?: Record<string, unknown>,
  options?: RequestOptions
) => sendRequest<T>('PATCH', url, data, options);

export const getFile = async (url: string, options: RequestOptions = {}) => {
  const token = await getToken();
  const headers: Record<string, string> = {
    'accept-language': 'en',
    'x-hakuba-platform': 'web',
    'x-hakuba-version': '1.0.0'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${url}`, {
    ...options,
    method: 'GET',
    headers
  });

  if (!response.ok) {
    throw new Error('File download failed');
  }

  const blob = await response.blob();

  const contentDisposition = response.headers.get('content-disposition');
  const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : undefined;

  return { blob, filename };
};

export const handleResponse = <T = unknown>(response: ApiResponse<T>): T => {
  if (!response?.success) {
    handleError(response);
    return null as unknown as T;
  }
  return response?.data as T;
};
